import { nearbyPlaceOptions } from "./viewModel";
import { Divider, StaticMapView, Text } from "../../components";
import { type Product } from "../../apis/products";
import { useTranslation } from "react-i18next";
import { Tools } from "../../utils";
import { useAuthStore } from "../../stores";
import { SVGs } from "../../assets";

export type ExpandedRowDetailsItem = {
  icon: string;
  label: string;
};

export function ExpandedRowDetails(product: Product) {
  const { t } = useTranslation();
  const userAvailableServices = useAuthStore((s) => s.user?.availableServices);
  const { lattitude, longitude, nearbyPlaces, availableServices } =
    product.partner_location ?? {};

  return (
    <div className="flex flex-col mt-[16px] gap-[16px]">
      <div className="flex flex-col gap-[16px] md:flex-row">
        <StaticMapView
          zoom={13}
          lat={lattitude ?? 0}
          lng={longitude ?? 0}
          size={{ width: 510, height: 256 }}
          className="rounded-[16px] bg-divider w-[420px] h-[210px] lg:w-[510px] lg:h-[256px]"
        />
        <div className="flex flex-col flex-1">
          <Text level={3} className="mb-[16px]">
            {t("general.description")}
          </Text>
          <Text className="text-text-secondary-light">
            {product.description ?? t("general.noDescriptionAvailable")}
          </Text>
          <Divider className="my-[16px]" />
          <Text level={3} className="mb-[16px]">
            {t("general.servicesAvailable")}
          </Text>
          <div className="grid grid-cols-4 gap-[16px]">
            {!availableServices?.length ? (
              <Text className="text-text-secondary-light">
                {t("dashboard.empty.noInPersonServices")}
              </Text>
            ) : (
              availableServices?.map((service, index) => {
                const meta = userAvailableServices?.find(
                  (s) => s.id === service,
                );
                if (!meta) return null;
                const { iconUrl, name } = meta;
                return (
                  <div key={index} className="flex items-center gap-[8px]">
                    {iconUrl && (
                      <img
                        src={iconUrl}
                        alt={name}
                        className="w-[24px] h-[24px]"
                      />
                    )}
                    <Text className="text-text-secondary-light">{name}</Text>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      {!!nearbyPlaces?.length && (
        <div>
          <Text level={3} className="my-[16px]">
            {t("general.nearbyReferences")}
          </Text>
          <div className="flex flex-col gap-[16px]">
            {nearbyPlaces?.map((place, index) => {
              const { icon: Icon } = nearbyPlaceOptions[place.type] ?? {
                icon: SVGs.Bus,
              };
              return (
                <div key={index} className="flex items-center gap-[8px]">
                  {!!Icon && <Icon className="w-[24px] h-[24px]" />}
                  <Text className="text-text-secondary-light">
                    {`${place.name}${Number.isFinite(place.distance) ? ` (${Tools.formatKm(place.distance / 1000)})` : ""}`}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
